import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchDiciplineById, updateMobileComplaintStatus } from "services/prosecutorDecipilineService";
import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";

export default function SingleMobileReuqest() {
  const { id } = useParams();
  const [mobileData, setMobileData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<string>("Pending");
  const [updating, setUpdating] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const resData = await fetchDiciplineById(id);
      setMobileData(resData as any);
      setStatus(resData?.status || "Pending");
      setError("");
    } catch (err) {
      setError("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusUpdate = async () => {
    if (!id) return;
    
    setUpdating(true);
    try {
      // Update status using the service function
      const updatedData = await updateMobileComplaintStatus(id, status);
      // Update the mobileData with new status
      setMobileData(updatedData);
      console.log("Status updated successfully");
      // Auto-refresh the data to ensure consistency
      await loadData();
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <h1 className="text-2xl font-semibold text-gray-700 flex items-center gap-2 mb-5">
        <span>📱</span>
        Accusation on Discipline Misconduct
      </h1>

      {mobileData && (
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Detail Card */}
          <div className="flex-1 min-w-0">
            <div className="ml-0 w-full rounded-2xl overflow-hidden mb-8 shadow-lg border-l-8 border-gray-600 bg-gradient-to-r from-white to-gray-50">
            {/* Subtle Gray Header */}
            <div className="bg-gradient-to-r from-gray-700 via-gray-600 to-gray-800 h-2"></div>
            
            <div className="p-6">
              {/* Complainant Section */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-50 rounded-lg">
                    <svg className="w-6 h-6 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Complainant Information
                  </h1>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-gray-800">
                    {mobileData.complainant_name || "-"}
                  </p>
                  {mobileData.complainant_phone && (
                    <p className="text-sm text-gray-600 mt-1">
                      {mobileData.complainant_phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Status Section */}
              <div className="mb-8 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <svg className="w-6 h-6 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Accusation Status</h2>
                      <p className="text-sm text-gray-600">Current complaint status</p>
                    </div>
                  </div>
                  <StatusChip status={mobileData.status || ""} />
                </div>

                {/* Grid Layout for Complaint Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Column 1 */}
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Report Application Number</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.report_application_number || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Complainant Phone</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.complainant_phone || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Complainant Email</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.complainant_email || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Accused Prosecutor</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.accused_prosecutor_name || "-"}
                      </p>
                    </div>
                  </div>

                  {/* Column 2 */}
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Accused Gender</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.accused_prosecutor_gender || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Date of Offense</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.date_of_offense || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Region</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.region || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Sub City</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.sub_city || "-"}
                      </p>
                    </div>
                  </div>

                  {/* Column 3 */}
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">City/Zone</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.city_zone || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">Kebele</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.kebele || mobileData.kebele_woreda || "-"}
                      </p>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-500">House Number</p>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {mobileData.house_number || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Offense Description */}
              {mobileData.offence_description && (
                <div className="mb-6 p-5 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <svg className="w-6 h-6 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Offense Description</h3>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {mobileData.offence_description}
                    </p>
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {mobileData.documents && mobileData.documents.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <svg className="w-6 h-6 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
                  </div>
                  <DocumentViewerModal
                    documents={mobileData.documents as any}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/complaint-termination-documents/document`}
                    layout="grid"
                    columns={4}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Decision Card - Right side */}
        <div className="w-full lg:w-80">
          <div className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm sticky top-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-50 rounded-lg">
                <svg className="w-6 h-6 text-red-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Decision</h3>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-3">Update Status</label>
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="Pending">Pending</option>
                <option value="Reviewed">Reviewed</option>
                <option value="Closed">Closed</option>
              </select>
              
              <button 
                className="mt-4 w-full px-4 py-2 bg-red-600 text-white font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400"
                onClick={handleStatusUpdate}
                disabled={updating}
              >
                {updating ? "Updating..." : "Submit Decision"}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
